import { MastraBase } from '../base';

import type {
  AgentsStorage,
  PromptBlocksStorage,
  ScorerDefinitionsStorage,
  MCPClientsStorage,
  MCPServersStorage,
  WorkspacesStorage,
  SkillsStorage,
  FavoritesStorage,
  ScoresStorage,
  WorkflowsStorage,
  MemoryStorage,
  ObservabilityStorage,
  BlobStore,
  DatasetsStorage,
  ExperimentsStorage,
  BackgroundTasksStorage,
  SchedulesStorage,
  ChannelsStorage,
  HarnessStorage,
  ToolProviderConnectionsStorage,
  NotificationsStorage,
  ThreadStateStorage,
  WorkflowDefinitionsStorage,
  KnowledgeStorage,
} from './domains';
import { InMemoryThreadStateStorage } from './domains/thread-state/inmemory';
import type { PruneOptions, PruneResult, RetentionConfig, TableRetentionPolicy } from './retention';

/** Map of all storage domain interfaces available in a composite store. */
export type StorageDomains = {
  workflows?: WorkflowsStorage;
  workflowDefinitions?: WorkflowDefinitionsStorage;
  scores?: ScoresStorage;
  memory?: MemoryStorage;
  channels?: ChannelsStorage;
  notifications?: NotificationsStorage;
  observability?: ObservabilityStorage;
  agents?: AgentsStorage;
  datasets?: DatasetsStorage;
  experiments?: ExperimentsStorage;
  promptBlocks?: PromptBlocksStorage;
  scorerDefinitions?: ScorerDefinitionsStorage;
  mcpClients?: MCPClientsStorage;
  mcpServers?: MCPServersStorage;
  workspaces?: WorkspacesStorage;
  skills?: SkillsStorage;
  favorites?: FavoritesStorage;
  blobs?: BlobStore;
  backgroundTasks?: BackgroundTasksStorage;
  schedules?: SchedulesStorage;
  harness?: HarnessStorage;
  toolProviderConnections?: ToolProviderConnectionsStorage;
  threadState?: ThreadStateStorage;
  knowledge?: KnowledgeStorage;
};

/**
 * Domain keys used by the Mastra Editor.
 * Used by the `editor` shorthand on MastraCompositeStoreConfig to route
 * all editor-related domains to a single store.
 */
export const EDITOR_DOMAINS = [
  'agents',
  'promptBlocks',
  'scorerDefinitions',
  'mcpClients',
  'mcpServers',
  'workspaces',
  'skills',
  'favorites',
  'toolProviderConnections',
] as const satisfies ReadonlyArray<keyof StorageDomains>;

/**
 * Every domain key of {@link StorageDomains}. Drives the composite store's
 * constructor resolution so no domain can be silently skipped by a
 * hand-maintained list. The exhaustiveness guard below turns a missing key
 * into a compile error.
 */
export const DOMAIN_KEYS = [
  'memory',
  'workflows',
  'workflowDefinitions',
  'scores',
  'observability',
  'agents',
  'datasets',
  'experiments',
  'promptBlocks',
  'scorerDefinitions',
  'mcpClients',
  'mcpServers',
  'workspaces',
  'skills',
  'favorites',
  'blobs',
  'backgroundTasks',
  'schedules',
  'channels',
  'harness',
  'toolProviderConnections',
  'notifications',
  'threadState',
  'knowledge',
] as const satisfies ReadonlyArray<keyof StorageDomains>;

/**
 * Compile-time exhaustiveness guard: if a key is added to `StorageDomains`
 * without being added to `DOMAIN_KEYS`, `MissingDomainKeys` stops being
 * `never` and the `true` assignment below becomes a type error.
 */
type MissingDomainKeys = Exclude<keyof StorageDomains, (typeof DOMAIN_KEYS)[number]>;
const _domainKeysExhaustive: MissingDomainKeys extends never ? true : never = true;
void _domainKeysExhaustive;

/**
 * Resolve a caller-supplied `orderBy` into a concrete `{ field, direction }`,
 * falling back to the defaults and rejecting fields outside `allowedFields`.
 * Storage adapters must interpolate only the returned values into SQL.
 */
export function resolveListOrderBy<TField extends string>(
  orderBy: { field?: TField; direction?: 'ASC' | 'DESC' } | undefined,
  allowedFields: readonly TField[],
  defaults: { field: TField; direction: 'ASC' | 'DESC' },
): { field: TField; direction: 'ASC' | 'DESC' } {
  const field = orderBy?.field ?? defaults.field;
  if (!allowedFields.includes(field)) {
    throw new Error(`Invalid orderBy field: ${String(field)}. Allowed: ${allowedFields.join(', ')}`);
  }
  const direction = orderBy?.direction ?? defaults.direction;
  if (direction !== 'ASC' && direction !== 'DESC') {
    throw new Error(`Invalid orderBy direction: ${String(direction)}`);
  }
  return { field, direction };
}

/**
 * Build a comparator for in-memory list sorting. Sorts by `field` in `direction`
 * (Dates compare by time, strings by locale-aware compare, nulls last) and breaks
 * ties by `id ASC` so pagination is stable across pages.
 */
export function compareByField<T extends { id: string }>(
  field: keyof T,
  direction: 'ASC' | 'DESC' = 'ASC',
): (a: T, b: T) => number {
  const sign = direction === 'DESC' ? -1 : 1;
  const compare = (left: unknown, right: unknown): number => {
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    if (left instanceof Date && right instanceof Date) return left.getTime() - right.getTime();
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return String(left).localeCompare(String(right));
  };
  return (a, b) => {
    const result = compare(a[field], b[field]);
    // Nulls stay last regardless of direction.
    if (a[field] == null || b[field] == null) return result || a.id.localeCompare(b.id);
    return result * sign || a.id.localeCompare(b.id);
  };
}

/**
 * Normalizes perPage input for pagination queries.
 *
 * @param perPageInput - The raw perPage value from the user
 * @param defaultValue - The default perPage value to use when undefined (typically 40 for messages, 100 for threads)
 * @returns A numeric perPage value suitable for queries (false becomes MAX_SAFE_INTEGER)
 * @throws Error if numeric perPage is not a finite, nonnegative integer
 */
export function normalizePerPage(perPageInput: number | false | undefined, defaultValue: number): number {
  if (perPageInput === false) {
    return Number.MAX_SAFE_INTEGER; // Get all results
  } else if (perPageInput === 0) {
    return 0; // Return zero results
  } else if (typeof perPageInput === 'number') {
    if (!Number.isInteger(perPageInput) || perPageInput < 0) {
      throw new Error('perPage must be >= 0');
    }
    return perPageInput;
  }
  // For undefined, use default
  return defaultValue;
}

/**
 * Calculates pagination offset and prepares perPage value for response.
 * When perPage is false (fetch all), offset is always 0 regardless of page.
 *
 * @param page - The page number (0-indexed)
 * @param perPageInput - The original perPage input (number, false for all, or undefined)
 * @param normalizedPerPage - The normalized perPage value (from normalizePerPage)
 * @returns Object with offset for query and perPage for response
 */
export function calculatePagination(
  page: number,
  perPageInput: number | false | undefined,
  normalizedPerPage: number,
): { offset: number; perPage: number | false } {
  if (!Number.isInteger(page) || page < 0) {
    throw new Error('page must be >= 0');
  }
  return {
    offset: perPageInput === false ? 0 : page * normalizedPerPage,
    perPage: perPageInput === false ? false : normalizedPerPage,
  };
}

/**
 * Configuration for individual domain overrides.
 * Each domain can be sourced from a different storage adapter.
 *
 * Set a domain to `false` to disable it entirely: the domain resolves to
 * `undefined` instead of falling back to the `editor`/`default` stores, so
 * nothing can read from or write to it through this composite.
 */
export type MastraStorageDomains = {
  [K in keyof StorageDomains]?: StorageDomains[K] | false;
};

/**
 * Configuration options for MastraCompositeStore.
 *
 * Can be used in two ways:
 * 1. By store implementations: `{ id, name, disableInit? }` - stores set `this.stores` directly
 * 2. For composition: `{ id, default?, domains?, disableInit? }` - compose domains from multiple stores
 */
export interface MastraCompositeStoreConfig {
  /**
   * Unique identifier for this storage instance.
   */
  id: string;

  /**
   * Name of the storage adapter (used for logging).
   * Required for store implementations extending MastraCompositeStore.
   */
  name?: string;

  /**
   * Default storage adapter to use for domains not explicitly specified.
   * If provided, domains from this storage will be used as fallbacks.
   */
  default?: MastraCompositeStore;

  /**
   * Storage adapter for editor-related domains (agents, promptBlocks, scorerDefinitions,
   * mcpClients, mcpServers, workspaces, skills).
   *
   * This is a shorthand that routes all editor domains to a single store instead of
   * specifying each individually in `domains`. Useful for filesystem-based storage
   * where editor configs are stored as JSON files in the repository.
   *
   * Priority: domains > editor > default
   *
   * @example
   * ```typescript
   * new MastraCompositeStore({
   *   id: 'my-store',
   *   default: postgresStore,
   *   editor: filesystemStore,
   * })
   * ```
   */
  editor?: MastraCompositeStore;

  /**
   * Individual domain overrides. Each domain can come from a different storage adapter.
   * These take precedence over both `editor` and `default` storage.
   *
   * @example
   * ```typescript
   * domains: {
   *   memory: pgStore.stores?.memory,
   *   workflows: libsqlStore.stores?.workflows,
   * }
   * ```
   */
  domains?: MastraStorageDomains;

  /**
   * When true, automatic initialization (table creation/migrations) is disabled.
   * This is useful for CI/CD pipelines where you want to:
   * 1. Run migrations explicitly during deployment (not at runtime)
   * 2. Use different credentials for schema changes vs runtime operations
   *
   * When disableInit is true:
   * - The storage will not automatically create/alter tables on first use
   * - You must call `storage.init()` explicitly in your CI/CD scripts
   *
   * @example
   * // In CI/CD script:
   * const storage = new PostgresStore({ ...config, disableInit: false });
   * await storage.init(); // Explicitly run migrations
   *
   * // In runtime application:
   * const storage = new PostgresStore({ ...config, disableInit: true });
   * // No auto-init, tables must already exist
   */
  disableInit?: boolean;

  /**
   * Opt-in, table-granular, age-based retention policies.
   *
   * Declare per-domain, per-table `maxAge` policies; call `storage.prune()`
   * to delete rows older than their configured age. Anything left unset is
   * kept forever (no behavior change by default).
   *
   * @example
   * ```typescript
   * retention: {
   *   memory: {
   *     messages: { maxAge: '30d' },
   *     threads: { maxAge: '90d' },
   *   },
   *   observability: {
   *     spans: { maxAge: '7d' },
   *   },
   * }
   * ```
   */
  retention?: RetentionConfig;
}

/**
 * Base class for all Mastra storage adapters.
 *
 * Can be used in two ways:
 *
 * 1. **Extended by store implementations** (PostgresStore, LibSQLStore, etc.):
 *    Store implementations extend this class and set `this.stores` with their domain implementations.
 *
 * 2. **Directly instantiated for composition**:
 *    Compose domains from multiple storage backends using `default` and `domains` options.
 *
 * All domain-specific operations should be accessed through `getStore()`:
 *
 * @example
 * ```typescript
 * // Composition: mix domains from different stores
 * const storage = new MastraCompositeStore({
 *   id: 'composite',
 *   default: pgStore,
 *   domains: {
 *     memory: libsqlStore.stores?.memory,
 *   },
 * });
 *
 * // Use `editor` shorthand to route all editor domains to a filesystem store
 * const storage2 = new MastraCompositeStore({
 *   id: 'with-fs-editor',
 *   default: pgStore,
 *   editor: filesystemStore,
 * });
 *
 * // Access domains
 * const memory = await storage.getStore('memory');
 * await memory?.saveThread({ thread });
 * ```
 */
/**
 * Minimal interface a storage adapter sees from the Mastra instance.
 * Kept narrow on purpose to avoid pulling the full Mastra type into the
 * storage layer (which would create a circular import).
 */
export interface StorageMastraRef {
  getAgentById?: (id: string) => { source?: string; __getEditorConfig?: () => unknown } | undefined;
  listAgents?: () => Record<string, { id: string; source?: string; __getEditorConfig?: () => unknown }> | undefined;
  getEditor?: () => { getSource?: () => 'code' | 'db' | undefined } | undefined;
}

/** A domain that implements the age-based retention `prune()` contract. */
interface PruneCapable {
  prune(policies: Record<string, TableRetentionPolicy>, options?: PruneOptions): Promise<PruneResult[]>;
}

function isPruneCapable(value: unknown): value is PruneCapable {
  return typeof value === 'object' && value !== null && typeof (value as PruneCapable).prune === 'function';
}

/** A store or domain that owns a client/connection handle it can release. */
interface Closable {
  close(): Promise<void>;
}

function isClosable(value: unknown): value is Closable {
  return typeof value === 'object' && value !== null && typeof (value as Closable).close === 'function';
}

export class MastraCompositeStore extends MastraBase {
  protected hasInitialized: null | Promise<boolean> = null;
  protected shouldCacheInit = true;

  id: string;
  stores?: StorageDomains;
  protected mastra?: StorageMastraRef;

  /**
   * When true, automatic initialization (table creation/migrations) is disabled.
   */
  disableInit: boolean = false;

  /**
   * Opt-in, table-granular, age-based retention policies. Consumed by
   * `prune()`. Undefined means nothing is pruned (keep forever).
   */
  protected retention?: RetentionConfig;

  /**
   * Retained references to the parent stores supplied via composition. `init()`
   * delegates to these so the parent's own `init()` logic (pragmas, ordered
   * DDL, init coalescing, etc.) runs instead of being bypassed by the
   * composite iterating the inner domains in parallel — which was the cause
   * of the SQLITE_BUSY / "no such table" races reported in issue #16782.
   */
  protected parentDefault?: MastraCompositeStore;
  protected parentEditor?: MastraCompositeStore;

  constructor(config: MastraCompositeStoreConfig) {
    const name = config.name ?? 'MastraCompositeStore';

    if (!config.id || typeof config.id !== 'string' || config.id.trim() === '') {
      throw new Error(`${name}: id must be provided and cannot be empty.`);
    }

    super({
      component: 'STORAGE',
      name,
    });

    this.id = config.id;
    this.disableInit = config.disableInit ?? false;
    this.retention = config.retention;

    // If composition config is provided (default, editor, or domains), compose the stores
    if (config.default || config.editor || config.domains) {
      const defaultStores = config.default?.stores;
      const editorStores = config.editor?.stores;
      const domainOverrides = config.domains ?? {};

      // Retain the parent store refs so init() can delegate to their own
      // init() — see field doc above and init() below.
      this.parentDefault = config.default;
      this.parentEditor = config.editor;

      // Validate that at least one storage source is provided (a `false`
      // override disables a domain, so it doesn't count as a source)
      const hasDefaultDomains = defaultStores && Object.values(defaultStores).some(v => v !== undefined);
      const hasEditorDomains = editorStores && Object.values(editorStores).some(v => v !== undefined);
      const hasOverrideDomains = Object.values(domainOverrides).some(v => v !== undefined && v !== false);

      if (!hasDefaultDomains && !hasEditorDomains && !hasOverrideDomains) {
        throw new Error(
          'MastraCompositeStore requires at least one storage source. Provide a default storage, an editor storage, or domain overrides.',
        );
      }

      const editorDomainSet = new Set<string>(EDITOR_DOMAINS);

      // Helper: resolve a domain with priority: domains > editor (for editor domains) > default.
      // A `false` override disables the domain — it resolves to undefined
      // instead of falling through to the editor/default stores.
      const resolve = <K extends keyof StorageDomains>(key: K): StorageDomains[K] | undefined => {
        const override: StorageDomains[K] | false | undefined = domainOverrides[key];
        if (override === false) return undefined;
        if (override !== undefined) return override;
        if (editorDomainSet.has(key) && editorStores?.[key] !== undefined) return editorStores[key];
        return defaultStores?.[key];
      };

      // Build the composed stores object by iterating the typed key list so
      // no domain can be dropped from a hand-maintained assignment block.
      const composed: Partial<StorageDomains> = {};
      const assign = <K extends keyof StorageDomains>(key: K): void => {
        composed[key] = resolve(key);
      };
      for (const key of DOMAIN_KEYS) {
        assign(key);
      }
      // Special case: the thread-state domain always has an in-memory store
      // wired by default so the built-in task tools work out of the box
      // without a configured backend. Configure a durable backend for state
      // that must survive a process restart. An explicit `false` override
      // still disables the domain entirely — the in-memory fallback only
      // applies when the domain is left unset (resolve already returned
      // undefined for a `false` override, so the guard below keeps disable
      // semantics intact).
      if (domainOverrides.threadState !== false) {
        composed.threadState = composed.threadState ?? new InMemoryThreadStateStorage();
      }
      this.stores = composed as StorageDomains;
    }
    // Otherwise, subclasses set stores themselves
  }

  /**
   * Register the Mastra instance with this storage adapter and cascade the
   * reference to all owned domain stores and parent composites. Storage
   * adapters that need to look up agents, editor config, etc. can read
   * `this.mastra` after this is called.
   * @internal
   */
  __registerMastra(mastra: StorageMastraRef, seen: Set<unknown> = new Set<unknown>()): void {
    if (seen.has(this)) return;
    seen.add(this);
    this.mastra = mastra;
    const cascade = (target: unknown) => {
      if (!target || typeof target !== 'object' || seen.has(target)) return;
      const fn = (target as { __registerMastra?: (m: StorageMastraRef, s?: Set<unknown>) => void }).__registerMastra;
      if (typeof fn === 'function') {
        fn.call(target, mastra, seen);
      } else {
        seen.add(target);
      }
    };
    if (this.parentDefault) cascade(this.parentDefault);
    if (this.parentEditor) cascade(this.parentEditor);
    if (this.stores) {
      for (const domain of Object.values(this.stores)) cascade(domain);
    }
  }

  /**
   * Get a domain-specific storage interface.
   *
   * @param storeName - The name of the domain to access ('memory', 'workflows', 'scores', 'observability', 'agents')
   * @returns The domain storage interface, or undefined if not available
   *
   * @example
   * ```typescript
   * const memory = await storage.getStore('memory');
   * if (memory) {
   *   await memory.saveThread({ thread });
   * }
   * ```
   */
  async getStore<K extends keyof StorageDomains>(storeName: K): Promise<StorageDomains[K] | undefined> {
    return this.stores?.[storeName];
  }

  /**
   * Delete rows older than their configured `maxAge` across all domains that
   * have a policy declared in `retention`.
   *
   * Prune is safe at scale: each domain deletes in bounded, batched, resumable,
   * cancellable chunks (see {@link PruneOptions}). It only deletes rows. On
   * SQLite/LibSQL freed pages are reused by future writes so the file stops
   * growing; handing disk back to the OS is left to the underlying database and
   * the operator to manage.
   *
   * Returns one {@link PruneResult} per table touched. A result with
   * `done: false` means eligible rows remain — call `prune()` again (e.g. on
   * the next cron tick) to continue.
   *
   * Prune is meant to run unattended (a cron tick), so a failure in one
   * domain is logged and skipped rather than rejecting the whole call — the
   * results already gathered for other domains are still returned, and the
   * failed domain is retried naturally on the next tick.
   *
   * With no `retention` configured this is a no-op returning `[]`.
   *
   * Pass `options.retention` to replace the configured retention policies for
   * this call only — e.g. to skip a domain (keep chat history) or prune more
   * aggressively than the standing config without reconstructing the store.
   */
  async prune(options?: PruneOptions): Promise<PruneResult[]> {
    const retention = options?.retention ?? this.retention;
    if (!retention) return [];

    const results: PruneResult[] = [];
    for (const [domainKey, tablePolicies] of Object.entries(retention) as [
      keyof StorageDomains,
      Record<string, TableRetentionPolicy> | undefined,
    ][]) {
      if (options?.signal?.aborted) break;
      if (!tablePolicies || Object.keys(tablePolicies).length === 0) continue;

      const domain = this.stores?.[domainKey];
      if (!isPruneCapable(domain)) continue; // domain not configured / doesn't support retention

      try {
        const domainResults = await domain.prune(tablePolicies, options);
        results.push(...domainResults);
      } catch (error) {
        this.logger?.error(`prune() failed for domain "${domainKey}"`, { error });
      }
    }
    return results;
  }

  /**
   * Initialize all domain stores.
   *
   * When a parent store was supplied via `default` or `editor`, delegate to
   * its own `init()` first. Each adapter owns its `init()` contract — it may
   * apply connection-level setup, run migrations, enforce DDL ordering, or
   * coalesce concurrent callers. Calling each domain's `init()` directly
   * against the parent's shared client would bypass all of that and can
   * corrupt or partially create schema (see issue #16782 for the SQLite
   * symptom).
   *
   * Any remaining domains that did NOT come from a parent (e.g. supplied via
   * the explicit `domains` override pointing at a different store) are then
   * initialized individually — but only the ones the parents didn't already
   * cover, so we never double-init the same domain instance.
   */
  async init(): Promise<void> {
    if (!this.shouldCacheInit) {
      await this.#runInit();
      return;
    }

    if (this.hasInitialized) {
      await this.hasInitialized;
      return;
    }

    const initPromise = this.#runInit().catch(error => {
      if (this.hasInitialized === initPromise) {
        this.hasInitialized = null;
      }
      throw error;
    });
    this.hasInitialized = initPromise;
    await initPromise;
  }

  async #runInit(): Promise<boolean> {
    // 1. Delegate to parent stores. Each parent owns its own init contract
    //    (setup, migrations, sequencing, coalescing). Dedupe by identity so
    //    a store passed as both `default` and `editor` only gets init()'d once.
    const uniqueParents = new Set<MastraCompositeStore>();
    if (this.parentDefault) uniqueParents.add(this.parentDefault);
    if (this.parentEditor) uniqueParents.add(this.parentEditor);
    await Promise.all([...uniqueParents].map(parent => parent.init()));

    // 2. Build a set of domain instances the parents already initialized so
    //    we don't init them a second time below.
    const alreadyInitialized = new Set<unknown>();
    const addParentDomains = (parent?: MastraCompositeStore) => {
      if (!parent?.stores) return;
      for (const domain of Object.values(parent.stores)) {
        if (domain) alreadyInitialized.add(domain);
      }
    };
    addParentDomains(this.parentDefault);
    addParentDomains(this.parentEditor);

    // 3. Init any remaining domains (typically those provided via the
    //    explicit `domains` override pointing at a different store, or those
    //    set directly by a subclass).
    const initTasks: Promise<void>[] = [];
    const maybeInit = (domain: { init(): Promise<void> } | undefined) => {
      if (!domain || alreadyInitialized.has(domain)) return;
      initTasks.push(domain.init());
      alreadyInitialized.add(domain);
    };

    if (this.stores) {
      // Iterate every registered domain instead of naming them one by one, so
      // a domain added to the stores map can never silently dodge init. The
      // typeof guard skips subclass-set entries that don't expose an init
      // method.
      for (const domain of Object.values(this.stores)) {
        if (typeof domain?.init === 'function') maybeInit(domain);
      }
    }

    await Promise.all(initTasks);
    return true;
  }
  /**
   * Lifecycle hook: release underlying client/connection handles.
   * Adapters (e.g. LibSQLStore) override this to checkpoint WAL files and close
   * the database client so OS handles are freed synchronously.
   * Called automatically by Mastra.shutdown().
   *
   * When used for composition this forwards to whatever it was built from: the
   * `default` and `editor` parents, plus any domain that owns a handle of its
   * own. Each target is closed once even when the same store backs several
   * domains, and a failure in one is logged and skipped so the remaining
   * handles are still released.
   */
  async close(): Promise<void> {
    const targets = new Map<Closable, string>();
    const collect = (candidate: unknown, label: string) => {
      if (!isClosable(candidate) || targets.has(candidate)) return;
      targets.set(candidate, label);
    };

    collect(this.parentDefault, 'default');
    collect(this.parentEditor, 'editor');

    // Domains that came from a parent are the parent's to close — closing
    // them here too could double-close a shared client. Mirrors the
    // already-initialized bookkeeping in #runInit().
    const parentOwned = new Set<unknown>();
    const addParentDomains = (parent?: MastraCompositeStore) => {
      if (!parent?.stores) return;
      for (const domain of Object.values(parent.stores)) {
        if (domain) parentOwned.add(domain);
      }
    };
    addParentDomains(this.parentDefault);
    addParentDomains(this.parentEditor);

    for (const [domainKey, domain] of Object.entries(this.stores ?? {})) {
      if (parentOwned.has(domain)) continue;
      collect(domain, `domain "${domainKey}"`);
    }

    await Promise.all(
      Array.from(targets, async ([target, label]) => {
        try {
          await target.close();
        } catch (error) {
          this.logger?.error(`close() failed for ${label} storage`, { error });
        }
      }),
    );
  }
}

/**
 * @deprecated Use MastraCompositeStoreConfig instead. This alias will be removed in a future version.
 */
export interface MastraStorageConfig extends MastraCompositeStoreConfig {}

/**
 * @deprecated Use MastraCompositeStore instead. This alias will be removed in a future version.
 */
export class MastraStorage extends MastraCompositeStore {}
